'use client'

import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { isEqual } from 'lodash'
import { v4 as uuid } from 'uuid'
import { Cell, Divider, InputText, Text } from '@vinted/web-ui'

import { useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import {
  flattenDynamicFilterOptions,
  transformDynamicFilterDefaultOptions,
  DynamicFilterOptionModel,
  ExperimentModel,
  LinksModel,
  SearchTranslationsModel,
  DynamicExposeType,
  OptionType,
  SelectionType,
  Filter,
  trackExperimentsByType,
  SELECT_LARGER_DROPDOWN_MIN_WIDTH,
  SELECT_LARGER_DROPDOWN_MAX_HEIGHT,
  TranslationModel,
} from '@marketplace-web/catalog/catalog-data'
import {
  useCatalogContext,
  DynamicFilterReducerAction,
  clickFilterEvent,
} from '@marketplace-web/catalog/catalog-page-data'
import {
  userClickDynamicFilterOptionEvent,
  userViewDynamicFilterOptionEvent,
} from '@marketplace-web/catalog/catalog-filters-data'

import useFetchDynamicFilterSearch from '../../../../../../hooks/useFetchDynamicFilterSearch'
import SelectableItemList, {
  RenderItemProps,
  SelectableListItem,
} from '../../../SelectableItemList'
import FilterDropdown from '../../../FilterDropdown'
import DynamicFilterCell from '../../common/DynamicFilterCell'
import FilterLoader from '../../common/FilterLoader'
import FilterNoResults from '../../common/FilterNoResults'
import FilterSelectionButton from '../../../FilterSelectionButton'

type ListOptionItem = SelectableListItem<DynamicFilterOptionModel>

type Props = {
  type: string
  title: string
  selectionType: SelectionType
  isSelectionHighlighted?: boolean
  isHidden?: boolean
  experiments?: Array<ExperimentModel>
  rootOptionType: OptionType
  links?: Array<LinksModel>
  options: Array<DynamicFilterOptionModel>
  selectedIds: Array<number>
  fromScreen: string
  searchTranslations?: SearchTranslationsModel
  translations?: TranslationModel
  totalItemsCount?: number | null
  totalCountMax?: number | null
  isAsyncFacet?: boolean
  isLazy?: boolean
  isLoading?: boolean
  onFilterClose: () => void
  onFilterOpen: (type: string) => void
  onListOptionSelect: (id: number) => void
  onSubmit: () => void
  onItemCountRecalculate?: (filterType: string) => void
}

const SearchFilterDropdown = ({
  type,
  title,
  selectionType,
  isSelectionHighlighted,
  isHidden,
  experiments,
  rootOptionType,
  links,
  options,
  selectedIds,
  fromScreen,
  searchTranslations,
  translations,
  totalItemsCount,
  totalCountMax,
  isAsyncFacet,
  isLazy,
  isLoading,
  onFilterClose,
  onFilterOpen,
  onListOptionSelect,
  onSubmit,
  onItemCountRecalculate,
}: Props) => {
  const { track } = useTracking()
  const trackAbTest = useTrackAbTestCallback()
  const fetchFilterSearch = useFetchDynamicFilterSearch()
  const {
    dispatch,
    state: {
      items: { searchTrackingParams },
      dynamicFilters: { filterSearchQuery },
    },
  } = useCatalogContext()
  const { globalSearchSessionId, globalCatalogBrowseSessionId } = searchTrackingParams
  const searchSessionId = searchTrackingParams.sessionId
  const seenItems = useRef<Array<number>>([])
  const [query, setQuery] = useState<string | null>(null)
  const [highlightedSelectedIds, setHighlightedSelectedIds] = useState<Array<number>>(selectedIds)
  const [forceClose, setForceClose] = useState(false)
  const [initialSelectedIds, setInitialSelectedIds] = useState<Array<number>>(selectedIds)
  const [showClearingLoader, setShowClearingLoader] = useState(false)
  const filteringSessionId = useMemo(() => uuid(), [])
  const noInputBox = !!isLazy && !query && options.length === 0
  const selectedTranslation = !!isLazy && !query ? translations : searchTranslations

  // only show clearing loader when options are empty AND we're clearing (not searching with results)
  const isClearingLoading = showClearingLoader && options.length === 0
  const isLoadingContent = isLoading || isClearingLoading

  useEffect(() => {
    if (experiments) trackExperimentsByType(experiments, DynamicExposeType.View, trackAbTest)
  }, [trackAbTest, experiments])

  const flattedOptions = useMemo(() => {
    return flattenDynamicFilterOptions(options)
  }, [options])

  const listItems = useMemo(() => {
    return transformDynamicFilterDefaultOptions(
      options,
      rootOptionType,
      isSelectionHighlighted,
      highlightedSelectedIds,
    )
  }, [options, rootOptionType, isSelectionHighlighted, highlightedSelectedIds])

  const getCurrentPosition = (id: number) =>
    flattedOptions.findIndex(option => option.id === id) + 1

  const handleOnFilterClick = () => {
    track(
      clickFilterEvent({
        filter: Filter.Dynamic,
        screen: 'search_filter',
        searchSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
        type,
        code: type,
      }),
    )

    if (experiments) trackExperimentsByType(experiments, DynamicExposeType.Click, trackAbTest)
  }

  const handleItemClick = (item: ListOptionItem) => {
    const id = Number(item.id)
    const position = getCurrentPosition(id)
    const itemsCount = item.data?.itemsCount

    onListOptionSelect(id)

    track(
      userClickDynamicFilterOptionEvent({
        id,
        type,
        code: type,
        position,
        isSelected: !selectedIds.includes(id),
        itemsCount,
        searchText: query || '',
        filteringSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
        searchSessionId,
        fromScreen,
      }),
    )

    if (experiments) trackExperimentsByType(experiments, DynamicExposeType.Apply, trackAbTest)
  }

  const getHandleItemView = (item: ListOptionItem) => (inView: boolean) => {
    if (!inView) return

    const id = Number(item.id)
    if (seenItems.current.includes(id)) return

    const position = getCurrentPosition(id)

    track(
      userViewDynamicFilterOptionEvent({
        id,
        type,
        code: type,
        position,
        itemsCount: item.data?.itemsCount,
        searchText: query || '',
        filteringSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
        searchSessionId,
        fromScreen,
      }),
    )

    seenItems.current = [...seenItems.current, id]
  }

  const handleOpenDropdown = () => {
    seenItems.current = []
    setQuery(null)
    setForceClose(false)
    setInitialSelectedIds(selectedIds)
    setShowClearingLoader(false)

    onFilterOpen(type)
  }

  const handleCloseDropdown = () => {
    setHighlightedSelectedIds(selectedIds)
    setForceClose(true)
    setShowClearingLoader(false)
    onSubmit()

    if (filterSearchQuery.code) {
      dispatch({
        type: DynamicFilterReducerAction.SET_FILTER_SEARCH_QUERY,
        payload: { text: null, code: null },
      })
    }

    if (query) setQuery('')
  }

  const handleAfterClose = () => {
    const wasForceClosed = forceClose
    onFilterClose()
    setForceClose(false)
    setHighlightedSelectedIds(selectedIds)
    setShowClearingLoader(false)

    if (!isEqual(selectedIds, initialSelectedIds) && !wasForceClosed) {
      // the ui state for filters will set to idle till the fetch is being started
      dispatch({
        type: DynamicFilterReducerAction.FETCH_DYNAMIC_FILTERS_IDLE,
        payload: undefined,
      })

      onSubmit()
    }

    if (query) setQuery('')
  }

  const handleSearchInput = (event: ChangeEvent<HTMLInputElement>) => {
    const searchQuery = event.currentTarget.value
    setQuery(searchQuery)

    if (searchQuery) {
      setShowClearingLoader(false)
      fetchFilterSearch({ text: searchQuery, code: type })
    }

    setHighlightedSelectedIds(selectedIds)
  }

  useEffect(() => {
    if (query !== '') return

    // clear up the query but not the code so the backend can reset the filters
    setShowClearingLoader(true)
    fetchFilterSearch({ text: null, code: type })
  }, [query, fetchFilterSearch, type])

  useEffect(() => {
    if (filterSearchQuery.text !== null) return

    // recalculate the total items when the search query is cleared
    onItemCountRecalculate?.(type)
    setQuery(null)
    setShowClearingLoader(false)
  }, [options, filterSearchQuery.text, type, onItemCountRecalculate])

  // hide loader when options populate
  useEffect(() => {
    if (options.length > 0) {
      setShowClearingLoader(false)
    }
  }, [options])

  const renderItemCell = ({
    item,
    itemElementProps,
  }: RenderItemProps<DynamicFilterOptionModel>) => (
    <DynamicFilterCell
      key={item.id}
      type={type}
      item={item}
      itemElementProps={itemElementProps}
      handleItemView={getHandleItemView(item)}
    />
  )

  const renderDropdown = () => {
    return (
      <div className="u-ui-padding-vertical-x-small">
        {!noInputBox && (
          <InputText
            value={query ?? ''}
            name={`${type}_filter_search`}
            placeholder={searchTranslations?.placeholder}
            onChange={handleSearchInput}
          />
        )}
        <ScrollableArea maxHeight={SELECT_LARGER_DROPDOWN_MAX_HEIGHT}>
          {listItems.length ? (
            <>
              <SelectableItemList
                name={`${type}_ids`}
                isMultiSelect={selectionType === SelectionType.Multi}
                items={listItems}
                selected={selectedIds}
                renderItem={renderItemCell}
                onItemClick={handleItemClick}
                disableButtonClicks
              />
              {links?.length && (
                <>
                  <Divider />
                  <Cell>
                    {links.map(link => (
                      <Text key={`${type}_link`} html text={link.body} as="p" />
                    ))}
                  </Cell>
                </>
              )}
            </>
          ) : (
            listItems.length === 0 &&
            query !== '' && <FilterNoResults translations={selectedTranslation} />
          )}
        </ScrollableArea>
      </div>
    )
  }

  if (isHidden) return null

  return (
    <div className="u-ui-margin-right-regular u-ui-margin-bottom-regular">
      <FilterDropdown
        title={title}
        areAnyOfItemsSelected={!!selectedIds.length}
        testId={`catalog--${type}-filter`}
        minWidth={SELECT_LARGER_DROPDOWN_MIN_WIDTH}
        onFilterClick={handleOnFilterClick}
        onOpen={handleOpenDropdown}
        onClose={handleAfterClose}
        useForceClose={forceClose}
        useChipAsTrigger
      >
        {isLoadingContent ? (
          <FilterLoader />
        ) : (
          <>
            <div className="u-ui-padding-vertical-x-small">{renderDropdown()}</div>
            <FilterSelectionButton
              onSubmit={handleCloseDropdown}
              totalItemsCount={totalItemsCount}
              maxItemsCount={totalCountMax}
              isAsync={isAsyncFacet}
              hideItemCount={query !== null}
            />
          </>
        )}
      </FilterDropdown>
    </div>
  )
}

export default SearchFilterDropdown
